import { Form, Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import { Grid } from 'ui-toolkit';
import { RootStore } from 'store/types';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import { mergeDocumentSchema } from './mergeDocumentSchema';
import { MergeDocumentModal } from './MergeDocumentModal';
import { SelectDocumentsContainer } from './SelectDocumentsContainer';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { PineconeThemeProvider } from '../../../../ui-toolkit/theming/PineconeThemeProvider';

export interface IMergeDocumentValues {
  selectedDocuments: IStandardDocumentVersionData[];
  showModal: boolean;
  fileName?: string;
  category?: IDocumentCategory;
}

interface IMergeDocumentFormProps {
  documents: IStandardDocumentVersionData[];
  orderId: string;
  onSubmit: (docIds: string[], fileName: string | undefined, category: IDocumentCategory) => void;
  onClose: () => void;
  searchValue: string | null;
  setSearchValue: (val: string | null) => void;
  setShowAddDocumentModal: (show: boolean) => void;
  children: React.ReactNode;
}

export const MergeDocumentForm: React.FC<IMergeDocumentFormProps> = ({
  orderId,
  children,
  documents,
  onSubmit,
  onClose,
  searchValue,
  setSearchValue,
  setShowAddDocumentModal,
}) => {
  const initialValues: IMergeDocumentValues = { selectedDocuments: [], showModal: false };

  const mergeDocumentsFetchStatus = useSelector(
    (state: RootStore) => state.order.documents.adminDocuments.mergeDocumentsFetchStatus
  );

  const handleSubmit = (values: IMergeDocumentValues) =>
    onSubmit(
      values.selectedDocuments.map((d) => d.id),
      values.fileName,
      values.category!
    );

  return (
    <Formik
      initialValues={initialValues}
      validatedOnMount={true}
      validationSchema={mergeDocumentSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form>
          {values.showModal && (
            <PineconeThemeProvider>
              <MergeDocumentModal
                fetchError={'There was an error combining documents.'}
                fetchStatus={mergeDocumentsFetchStatus}
                onClose={onClose}
              />
            </PineconeThemeProvider>
          )}
          <Grid>
            <SelectDocumentsContainer
              documents={documents}
              orderId={orderId}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setShowAddDocumentModal={setShowAddDocumentModal}
            />
            {children}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
